/**
 * Geração de PDF e impressão de OS a partir de dados de ordem (ex.: ordem do Board).
 * Layout alinhado ao usado no OpenOSModal (abertura de OS).
 * Uso: generateOSPdfFromOrder(order), printOSFromOrder(order).
 */

import jsPDF from 'jspdf';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getBoardClienteExibicao } from '../../board/utils/clienteBoardDisplay';

/** Alinhado ao limite do backend (413). */
const MAX_HTML_FOR_PDF_BYTES = 2 * 1024 * 1024;

function utf8ByteLength(s) {
  return new TextEncoder().encode(s).length;
}

/**
 * Envia o mesmo HTML do layout V2 ao servidor; resposta é PDF em memória (sem arquivo no disco).
 * @param {string} filenameOptional - ex.: "OS-123.pdf"
 */
async function fetchOsPdfFromServer(html, filenameOptional) {
  if (typeof html !== 'string' || html.length === 0) {
    throw new Error('HTML vazio.');
  }
  if (utf8ByteLength(html) > MAX_HTML_FOR_PDF_BYTES) {
    throw new Error('Conteúdo muito grande para gerar PDF.');
  }
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (!token) {
    throw new Error('Sessão expirada. Faça login novamente.');
  }
  const base = getApiBaseUrl();
  const body = { html };
  if (filenameOptional) {
    body.filename = filenameOptional.endsWith('.pdf') ? filenameOptional : `${filenameOptional}.pdf`;
  }
  const res = await fetch(`${base}/api/board/orders/render-pdf`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `Erro ao gerar PDF (${res.status})`;
    try {
      const j = await res.json();
      if (j && j.message) msg = j.message;
    } catch (_) {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.blob();
}

const MARGIN = 15;
const MARGIN_RIGHT_HEADER = 2; // margem extra à direita no header para o número da OS não cortar

/** Contadores do histórico (TB02122): milhar/milhões no padrão pt-BR. */
function formatContadorHistorico(val) {
  if (val == null || val === '') return '';
  if (typeof val === 'number' && Number.isFinite(val)) {
    return Math.trunc(val).toLocaleString('pt-BR');
  }
  const raw = String(val).trim();
  if (!raw) return '';
  const normalized = raw.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  if (!Number.isFinite(n)) return raw;
  return Math.trunc(n).toLocaleString('pt-BR');
}

const HISTORICO_TD = 'border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top;';
const HISTORICO_TD_R = `${HISTORICO_TD} border-right:1.5pt solid #d0cece;`;
const HISTORICO_P = 'margin:0; font-size:6pt; line-height:1.12; font-family:Inter;';

function buildHistoricoTableRowsHtml(limited, esc) {
  return limited
    .map((ordem) => {
      const os = esc(ordem.TB02115_CODIGO || '');
      const abertura = esc(ordem.TB02115_DATA || '');
      const motivo = esc(ordem.TB02115_NOME || '');
      const tecnico = esc(ordem.TB01024_NOME || '');
      const fechamento = esc(ordem.TB02122_DATAFIM || '');
      const a4pb = esc(formatContadorHistorico(ordem.TB02122_CONTADOR));
      const a4cor = esc(formatContadorHistorico(ordem.TB02122_CONTADORC));
      const condicao = esc(ordem.TB01055_NOME || '');
      const laudo = esc(ordem.TB02122_OBS || '');
      return `<tr>
  <td style="width:6%; ${HISTORICO_TD_R}"><p style="${HISTORICO_P}">${os}</p></td>
  <td style="width:8%; ${HISTORICO_TD_R}"><p style="${HISTORICO_P}">${abertura}</p></td>
  <td style="width:18%; ${HISTORICO_TD_R}"><p style="${HISTORICO_P}">${motivo}</p></td>
  <td style="width:10%; ${HISTORICO_TD_R}"><p style="${HISTORICO_P}">${tecnico}</p></td>
  <td style="width:8%; ${HISTORICO_TD_R}"><p style="${HISTORICO_P}">${fechamento}</p></td>
  <td style="width:9%; ${HISTORICO_TD_R}"><p style="${HISTORICO_P} text-align:right;">${a4pb}</p></td>
  <td style="width:9%; ${HISTORICO_TD_R}"><p style="${HISTORICO_P} text-align:right;">${a4cor}</p></td>
  <td style="width:11%; ${HISTORICO_TD_R}"><p style="${HISTORICO_P}">${condicao}</p></td>
  <td style="width:21%; ${HISTORICO_TD} border-left:1.5pt solid #d0cece;"><p style="${HISTORICO_P}">${laudo}</p></td>
</tr>`;
    })
    .join('');
}

const HISTORICO_TABLE_HEADER_TR = `<tr>
        <td style="width:6%; border-right:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">OS</p>
        </td>
        <td style="width:8%; border-right:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">Data</p>
        </td>
        <td style="width:18%; border-right:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">Defeito</p>
        </td>
        <td style="width:10%; border-right:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">Técnico</p>
        </td>
        <td style="width:8%; border-right:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">Fechamento</p>
        </td>
        <td style="width:9%; border-right:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">A4 P&amp;B</p>
        </td>
        <td style="width:9%; border-right:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">A4 Cor</p>
        </td>
        <td style="width:11%; border-right:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">Condição</p>
        </td>
        <td style="width:21%; border-left:1.5pt solid #d0cece; border-bottom:1.5pt solid #d0cece; padding:1pt 2pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:6pt; font-family:Inter; font-weight:bold;">Laudo</p>
        </td>
      </tr>`;

function historicoTableBlock(rowsHtml) {
  return `<p style="font-size:15pt; font-weight:bold; color:#404040; margin-bottom:5pt;">Histórico de atendimento</p>
    <table class="historico-os-table" cellspacing="0" cellpadding="0" style="width:100%; margin-bottom:0; border:1.5pt solid #d0cece; border-collapse:collapse; table-layout:fixed;">
      ${HISTORICO_TABLE_HEADER_TR}
      ${rowsHtml}
    </table>`;
}
const PRIMARY_COLOR = [90, 61, 196];
const TEXT_COLOR = [45, 55, 72];
const LIGHT_GRAY = [226, 232, 240];

/**
 * Mapeia código do tipo de OS (board/API) para rótulo em maiúsculas (TIPO: CORRETIVA, etc.)
 * @param {string} tipo - Código (E, B, A, R, D, I, S, N, C)
 * @returns {string}
 */
export function getTipoOsLabelForPrint(tipo) {
  const types = {
    E: 'ESTOQUE',
    B: 'BALCÃO',
    A: 'AFERIÇÃO',
    R: 'RETORNO/RECARGA',
    D: 'DESINSTALAÇÃO',
    I: 'INSTALAÇÃO',
    S: 'PREVENTIVA',
    N: 'CORRETIVA',
    C: 'CORRETIVA'
  };
  return types[tipo] || 'CORRETIVA';
}

/**
 * Normaliza objeto order (ex.: ordem do Board) para campos usados no layout.
 * Para reimpressão da OS (Board – Em aberto / Técnicos): nome da empresa, CNPJ e endereço
 * vêm dos campos TB01007_NOME, TB01007_CNPJ e TB01007_ENDERECO.
 * @param {object} order - Ordem com id, cliente, TB01007_NOME, TB01007_CNPJ, TB01007_ENDERECO, equipamento, etc.
 * @returns {object}
 */
function buildIdentificacaoEquipamentoForPrint(order, pickFirstFilled) {
  const pick =
    pickFirstFilled ||
    ((...values) => {
      for (const value of values) {
        if (value == null) continue;
        const normalized = String(value).trim();
        if (normalized) return normalized;
      }
      return '';
    });
  const serie = pick(
    order?.numeroSerie,
    order?.TB02115_NUMSERIE,
    order?.serie,
    order?.numSerie
  );
  const patrimonio = pick(order?.patrimonio, order?.TB02112_PAT);
  if (serie && patrimonio) return `${serie} · ${patrimonio}`;
  if (serie) return serie;
  if (patrimonio) return patrimonio;
  return 'Não informado';
}

function normalizeOrderForLayout(order) {
  if (!order) return {};
  const empNome = (order.TB01007_NOME || order.emp_nome || order.empresa || '').toString().trim();
  const empCnpj = (order.TB01007_CNPJ || order.emp_cnpj || order.cnpj || '').toString().trim();
  const empEndereco = (order.TB01007_ENDERECO || order.emp_endereco || '').toString().trim();
  let enderecoText = (order.endereco || '').toString().trim();
  if (order.TB02112_LOCAL && String(order.TB02112_LOCAL).trim()) {
    enderecoText += ` - ${String(order.TB02112_LOCAL).trim()}`;
  }
  const pickFirstFilled = (...values) => {
    for (const value of values) {
      if (value == null) continue;
      const normalized = String(value).trim();
      if (normalized) return normalized;
    }
    return '';
  };
  const identificacao = buildIdentificacaoEquipamentoForPrint(order, pickFirstFilled);
  return {
    empresaNome: empNome || 'NOME DA EMPRESA',
    empCnpj,
    empEndereco,
    osNumber: order.id != null ? String(order.id) : '000000',
    tipoLabel: getTipoOsLabelForPrint(order.tipo),
    identificacao,
    cliente: getBoardClienteExibicao(order),
    enderecoText,
    equipamentoText: (order.equipamento || '').toString().trim(),
    siteNome: (order.site_nome || '').toString().trim(),
    deptoNome: (order.depto_nome || '').toString().trim(),
    dataFormatada: (order.dataAbertura || '').toString().trim() || formatToday(),
    solicitanteText: (order.solicitante || '').toString().trim(),
    contatoText: '',
    defeitoNome: (order.defeitoNome ?? (order.motivoOS || '')).toString().trim(),
    defeitoObservacao: (order.defeitoObservacao ?? (order.motivoOSObservacao || '')).toString().trim(),
    // Data de previsão: apenas para OS do grupo FUTURA, em formato dd/MM/yyyy (sem hora/fuso)
    atendimentoDate:
      order.forecastGroup === 'FUTURA' && order.groupForecastDate
        ? formatDateOnlyBR(order.groupForecastDate)
        : null
  };
}

function formatToday() {
  const t = new Date();
  const d = String(t.getDate()).padStart(2, '0');
  const m = String(t.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}/${t.getFullYear()}`;
}

/** Formata YYYY-MM-DD para dd/MM/yyyy (BR) sem usar Date, para não alterar por fuso. */
function formatDateOnlyBR(isoDateStr) {
  if (!isoDateStr || typeof isoDateStr !== 'string') return null;
  const part = isoDateStr.trim().split('T')[0];
  const m = part.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function buildLocalizacao(order, normalized) {
  const localEquip = (order && order.TB02112_LOCAL ? String(order.TB02112_LOCAL) : '').trim();
  const localOs = (order && order.TB02115_LOCAL ? String(order.TB02115_LOCAL) : '').trim();
  const siteRaw = (normalized.siteNome || '').trim();
  const deptoRaw = (normalized.deptoNome || '').trim();

  const parts = [];

  if (localEquip) {
    parts.push(localEquip);
  } else if (localOs) {
    parts.push(localOs);
  }

  if (siteRaw) parts.push(siteRaw);
  if (deptoRaw) parts.push(deptoRaw);

  return parts.join(' ');
}

function formatDateOnlyBrFromString(value) {
  if (!value) return null;
  const s = String(value).trim();

  // Já vem em dd/MM/yyyy ou dd/MM/yyyy HH:mm
  const slashMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) {
    return `${slashMatch[1]}/${slashMatch[2]}/${slashMatch[3]}`;
  }

  // Formato ISO yyyy-MM-dd ou yyyy-MM-ddTHH:mm...
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }

  return s || null;
}

function getHistoricoFechamentoTimestamp(value) {
  if (!value) return Number.NEGATIVE_INFINITY;
  const s = String(value).trim();
  if (!s) return Number.NEGATIVE_INFINITY;

  // dd/MM/yyyy[ HH:mm[:ss]]
  const brMatch = s.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (brMatch) {
    const [, dd, mm, yyyy, hh = '00', mi = '00', ss = '00'] = brMatch;
    const ts = Date.UTC(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss)
    );
    return Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts;
  }

  // yyyy-MM-dd[THH:mm[:ss]] ou yyyy-MM-dd[ HH:mm[:ss]]
  const isoLike = s.replace(' ', 'T');
  const parsed = Date.parse(isoLike);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

function sortHistoricoByFechamentoDesc(list) {
  return [...list].sort((a, b) => {
    const tsA = getHistoricoFechamentoTimestamp(a && a.TB02122_DATAFIM);
    const tsB = getHistoricoFechamentoTimestamp(b && b.TB02122_DATAFIM);
    return tsB - tsA;
  });
}

function getPrevisaoLabelForPdf(order, normalized) {
  if (order && order.previsaoLabelOverride != null && String(order.previsaoLabelOverride).trim() !== '') {
    return String(order.previsaoLabelOverride).trim();
  }
  const forecastGroup = order && order.forecastGroup;

  // 1) Em aberto: sempre "Sem previsão"
  if (!forecastGroup || forecastGroup === 'Em Aberto' || forecastGroup === 'Em aberto') {
    return 'Sem previsão';
  }

  // 2) Previsto para hoje: data atual (dd/MM/yyyy)
  if (forecastGroup === 'Previsto para hoje') {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // 3) Previstas para amanhã: próximo dia útil (mesma inteligência do RouteModal)
  if (forecastGroup === 'Previstas para amanhã') {
    const today = new Date();
    const currentDay = today.getDay();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();

    let forecastDate;
    if (currentDay >= 1 && currentDay <= 4) {
      // Segunda a quinta: próximo dia
      forecastDate = new Date(year, month, day + 1);
    } else {
      // Sexta, sábado ou domingo: próxima segunda
      const daysUntilMonday = currentDay === 0 ? 1 : (8 - currentDay);
      forecastDate = new Date(year, month, day + daysUntilMonday);
    }

    const dd = String(forecastDate.getDate()).padStart(2, '0');
    const mm = String(forecastDate.getMonth() + 1).padStart(2, '0');
    const yyyy = forecastDate.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  // 4) FUTURA: usar data de grupo (dia da carteira futura) ou "Sem previsão"
  if (forecastGroup === 'FUTURA') {
    if (normalized.atendimentoDate) {
      return normalized.atendimentoDate;
    }
    return 'Sem previsão';
  }

  // Demais casos: usar campo previsao se existir (já pode vir de rotas) apenas com data
  const rawPrevisao = order && order.previsao;
  if (rawPrevisao) {
    const formatted = formatDateOnlyBrFromString(rawPrevisao);
    return formatted || 'Sem previsão';
  }

  // Sem campo de previsão definido
  return 'Sem previsão';
}

/**
 * Gera e faz download do PDF da OS a partir dos dados da ordem.
 * @param {object} order - Ordem (ex.: ordem do Board ao abrir detalhes)
 */
export function generateOSPdfFromOrder(order) {
  const d = normalizeOrderForLayout(order);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = MARGIN;

  const addPageNumbers = () => {
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(150, 150, 150);
      const pageText = `${i} / ${totalPages}`;
      pdf.text(pageText, pageWidth - margin - pdf.getTextWidth(pageText), pageHeight - 10);
    }
  };

  let yPosition = margin;

  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...PRIMARY_COLOR);
  pdf.text(d.empresaNome, margin, yPosition);

  yPosition += 8;
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  if (d.empCnpj) {
    pdf.text(`CNPJ: ${d.empCnpj}`, margin, yPosition);
    yPosition += 5;
  }
  if (d.empEndereco) {
    pdf.text(`Endereço: ${d.empEndereco}`, margin, yPosition);
  }

  const osNumberText = `OS ${d.osNumber}`;
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  const osNumberWidth = pdf.getTextWidth(osNumberText);
  const osNumberY = margin;
  const rightEdge = pageWidth - margin - MARGIN_RIGHT_HEADER;
  pdf.setTextColor(...PRIMARY_COLOR);
  pdf.text(osNumberText, rightEdge - osNumberWidth, osNumberY);

  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  const tipoText = `TIPO: ${d.tipoLabel}`;
  const tipoTextWidth = pdf.getTextWidth(tipoText);
  const spacing = 6;
  let xCurrent = rightEdge;
  xCurrent -= tipoTextWidth;
  pdf.setFont('helvetica', 'normal');
  pdf.text(tipoText, xCurrent, osNumberY + 7);
  xCurrent -= spacing;
  let headerRightOffset = 14;

  if (d.identificacao) {
    pdf.setFont('helvetica', 'bold');
    const idNumWidth = pdf.getTextWidth(d.identificacao);
    xCurrent -= idNumWidth;
    pdf.text(d.identificacao, xCurrent, osNumberY + 7);
    xCurrent -= pdf.getTextWidth('Identificação: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text('Identificação: ', xCurrent, osNumberY + 7);
  }

  if (d.atendimentoDate) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const dateTextWidth = pdf.getTextWidth(d.atendimentoDate);
    const badgeWidth = dateTextWidth + 4;
    const badgeHeight = 5;
    const badgeX = rightEdge - badgeWidth;
    const badgeY = osNumberY + headerRightOffset;
    pdf.setFillColor(242, 242, 242);
    pdf.rect(badgeX, badgeY, badgeWidth, badgeHeight, 'F');
    pdf.text(d.atendimentoDate, badgeX + 2, badgeY + 3.5);
  }

  yPosition += d.empEndereco || d.empCnpj ? 5 : 5;
  pdf.setDrawColor(128, 128, 128);
  pdf.setLineWidth(0.25);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(38, 50, 56);
  pdf.text('Cliente e equipamento', margin, yPosition);
  yPosition += 2;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...TEXT_COLOR);

  if (d.cliente) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Cliente: ', margin, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(d.cliente, margin + pdf.getTextWidth('Cliente: '), yPosition);
    yPosition += 6;
  }
  if (d.enderecoText) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Endereço: ', margin, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(d.enderecoText, margin + pdf.getTextWidth('Endereço: '), yPosition);
    yPosition += 6;
  }
  if (d.equipamentoText) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Equipamento: ', margin, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(d.equipamentoText, margin + pdf.getTextWidth('Equipamento: '), yPosition);
    yPosition += 6;
  }
  if (d.siteNome || d.deptoNome) {
    if (d.siteNome) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Site: ', margin, yPosition);
      pdf.setFont('helvetica', 'normal');
      pdf.text(d.siteNome, margin + pdf.getTextWidth('Site: '), yPosition);
    }
    if (d.deptoNome) {
      const siteW = d.siteNome ? pdf.getTextWidth('Site: ' + d.siteNome) : 0;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Departamento: ', margin + siteW + 15, yPosition);
      pdf.setFont('helvetica', 'normal');
      pdf.text(d.deptoNome, margin + siteW + 15 + pdf.getTextWidth('Departamento: '), yPosition);
    }
    yPosition += 6;
  }

  yPosition += 8;
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(38, 50, 56);
  pdf.text('Dados para atendimento', margin, yPosition);
  yPosition += 2;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  const rowHeight = 8;
  pdf.setFillColor(...LIGHT_GRAY);
  pdf.rect(margin, yPosition - 5, pageWidth - margin * 2, rowHeight, 'F');
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text('DATA', margin + 2, yPosition);
  pdf.text('SOLICITANTE', margin + 42, yPosition);
  pdf.text('DEFEITO', margin + 112, yPosition);
  yPosition += rowHeight;

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(d.dataFormatada, margin + 2, yPosition);
  pdf.text(d.solicitanteText + d.contatoText, margin + 42, yPosition);
  pdf.text(d.defeitoNome, margin + 112, yPosition);
  yPosition += rowHeight + 5;

  if (d.defeitoObservacao) {
    pdf.setFillColor(247, 250, 252);
    pdf.rect(margin, yPosition - 3, pageWidth - margin * 2, 15, 'F');
    pdf.setFontSize(9);
    const lines = pdf.splitTextToSize(d.defeitoObservacao, pageWidth - margin * 2 - 4);
    pdf.text(lines, margin + 2, yPosition + 3);
    yPosition += 18;
  }

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(38, 50, 56);
  pdf.text('Laudo técnico', margin, yPosition);
  yPosition += 2;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  const laudoLineHeight = 7;
  const laudoLinesCount = 4;

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text('Condição do equipamento após intervenção', margin + 2, yPosition);
  yPosition += 5;
  for (let i = 0; i < laudoLinesCount; i++) {
    pdf.setDrawColor(...LIGHT_GRAY);
    pdf.setLineWidth(0.1);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += laudoLineHeight;
  }
  yPosition += 4;

  pdf.setFont('helvetica', 'bold');
  pdf.text('Observações', margin + 2, yPosition);
  yPosition += 5;
  for (let i = 0; i < laudoLinesCount; i++) {
    pdf.setDrawColor(...LIGHT_GRAY);
    pdf.setLineWidth(0.1);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += laudoLineHeight;
  }

  yPosition += 5;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Data e hora inicial: ___/___/____ __:__', margin, yPosition);
  pdf.text('Data e hora final: ___/___/____ __:__', margin + pdf.getTextWidth('Data e hora inicial: ___/___/____ __:__') + 15, yPosition);
  yPosition += 10;

  pdf.setDrawColor(...PRIMARY_COLOR);
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  const signatureWidth = (pageWidth - margin * 2 - 10) / 2;
  let sy = yPosition;
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...PRIMARY_COLOR);
  pdf.text('TÉCNICO RESPONSÁVEL', margin, sy);
  sy += 6;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text('Nome:', margin, sy);
  sy += 8;
  pdf.setDrawColor(...LIGHT_GRAY);
  pdf.setLineWidth(0.2);
  pdf.line(margin, sy, margin + signatureWidth, sy);
  sy += 8;
  pdf.text('Assinatura:', margin, sy);
  pdf.line(margin, sy + 8, margin + signatureWidth, sy + 8);

  let cy = yPosition;
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...PRIMARY_COLOR);
  pdf.text('SOLICITANTE/CLIENTE', margin + signatureWidth + 10, cy);
  cy += 6;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text('Nome:', margin + signatureWidth + 10, cy);
  cy += 8;
  pdf.setDrawColor(...LIGHT_GRAY);
  pdf.line(margin + signatureWidth + 10, cy, pageWidth - margin, cy);
  cy += 8;
  pdf.text('Assinatura:', margin + signatureWidth + 10, cy);
  pdf.line(margin + signatureWidth + 10, cy + 8, pageWidth - margin, cy + 8);

  addPageNumbers();
  pdf.save(`OS_${d.osNumber}.pdf`);
}

/**
 * Monta o HTML do layout principal (OS + histórico, duas páginas). Usado por Gerar PDF (download) e Imprimir.
 * @param {object} order - Ordem atual (Board)
 * @returns {Promise<{ html: string, osNumber: string }>}
 */
export async function buildOSPdfV2Html(order) {
  const d = normalizeOrderForLayout(order);
  const esc = (s) =>
    (s || '')
      .toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const empresaNome = esc(d.empresaNome);
  const empCnpj = esc(d.empCnpj);
  const empEndereco = esc(d.empEndereco);
  const osNumber = esc(d.osNumber);
  const tipoLabel = esc(d.tipoLabel);
  const identificacao = esc(d.identificacao);
  const cliente = esc(d.cliente);
  const enderecoCliente = esc(d.enderecoText);
  const equipamentoText = esc(d.equipamentoText);
  const siteNome = esc(d.siteNome);
  const deptoNome = esc(d.deptoNome);
  const dataFormatada = esc(d.dataFormatada);
  const solicitanteText = esc(d.solicitanteText);
  const defeitoNome = esc(d.defeitoNome);
  const defeitoObservacao = esc(d.defeitoObservacao).replace(/\n/g, ' ');
  const atendimentoDate = d.atendimentoDate ? esc(d.atendimentoDate) : '';
  const localizacao = esc(buildLocalizacao(order || {}, d));
  const previsaoLabel = esc(getPrevisaoLabelForPdf(order || {}, d));

  let historicoRowsHtml = '';
  try {
    const numeroSerie =
      (order && (order.numeroSerie || order.TB02115_NUMSERIE || order.serie)) || '';
    const trimmedSerie = (numeroSerie || '').toString().trim();
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;

    if (trimmedSerie && token) {
      const base = getApiBaseUrl();
      const res = await fetch(
        `${base}/api/oficina/ordens-serie?serie=${encodeURIComponent(trimmedSerie)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (res.ok) {
        const data = await res.json();
        const list = data && data.success && Array.isArray(data.data) ? data.data : [];
        const sorted = sortHistoricoByFechamentoDesc(list);
        const limited = sorted.slice(0, 6);
        historicoRowsHtml = buildHistoricoTableRowsHtml(limited, esc);
      }
    }
  } catch (error) {
    console.error('Erro ao carregar histórico para PDF:', error);
  }

  if (!historicoRowsHtml) {
    historicoRowsHtml = `
<tr>
  <td colspan="9" style="border:1.5pt solid #d0cece; padding:3pt; font-size:7pt; font-family:Inter; text-align:center;">Nenhum histórico de atendimento encontrado.</td>
</tr>`;
  }

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>OS ${osNumber} - Layout 2</title>
  <style>
    @page { margin: 8mm; }
    body { line-height:108%; font-family: Inter, Calibri, Arial, sans-serif; font-size:11pt; margin:0; padding:0; }
    h1, h2, h3, h4, h5, h6, p { margin:0 0 8pt; }
    table { margin-top:0; margin-bottom:8pt; border-collapse:collapse; }
    .page { page-break-after: always; width: 210mm; height: 297mm; box-sizing: border-box; overflow: hidden; }
    .page:last-child { page-break-after: auto; }
    .heading-section-title {
      font-size:16pt;
      font-weight:bold;
      color:#404040;
      margin-top:4pt;
      margin-bottom:4pt;
    }
    .label { font-family: Inter, Calibri, Arial, sans-serif; }
    .label-strong { font-weight:bold; }
    table.historico-os-table { table-layout: fixed; width: 100%; border-collapse: collapse; }
    table.os-header-table { table-layout: fixed; width: 100%; border-collapse: collapse; }
    .os-header-meta { word-wrap: break-word; overflow-wrap: anywhere; }
  </style>
</head>
<body>
  <div class="page">
    <table cellspacing="0" cellpadding="0" class="os-header-table" style="width:100%; margin-bottom:0; border-collapse:collapse">
      <colgroup>
        <col style="width:20%" />
        <col style="width:20%" />
        <col style="width:20%" />
        <col style="width:40%" />
      </colgroup>
      <tr>
        <td colspan="3" style="padding:4mm 5.4pt 0 5.4pt; vertical-align:top">
          <p style="margin:0; font-size:12pt; white-space:nowrap;">
            <span style="font-family:Inter; font-weight:bold; color:#6600ff">${empresaNome}</span>
          </p>
        </td>
        <td style="padding:4mm 5.4pt 0 5.4pt; vertical-align:top; text-align:right">
          <p style="margin:0; font-size:22pt;">
            <span style="font-family:Inter; font-weight:bold; color:#6600ff">${osNumber}</span>
          </p>
        </td>
      </tr>
      <tr>
        <td colspan="3" style="border-bottom:1.5pt solid #aeaaaa; padding:0 5.4pt; vertical-align:top">
          <p style="margin:0; font-size:11pt">
            <span class="label">CNPJ</span><span class="label-strong"> ${empCnpj}</span>
          </p>
          <p style="margin:0; font-size:11pt; white-space:nowrap;">
            <span class="label-strong">${empEndereco}</span>
          </p>
        </td>
        <td class="os-header-meta" style="border-bottom:1.5pt solid #aeaaaa; padding:0 5.4pt; vertical-align:top; text-align:right">
          <p style="margin:0; font-size:9pt; line-height:1.35;">
            <span class="label">Identificação:</span><span class="label-strong"> ${identificacao}</span>
          </p>
          <p style="margin:0; font-size:9pt; line-height:1.35;">
            <span class="label">Tipo:</span><span class="label-strong"> ${tipoLabel}</span>
          </p>
        </td>
      </tr>
      <tr>
        <td colspan="4" style="padding:0 5.4pt; vertical-align:top">
          <p style="margin:0; font-size:16pt; margin-top:10pt;">
            <span class="label-strong" style="color:#404040">Cliente e equipamento</span>
          </p>
        </td>
      </tr>
      <tr>
        <td colspan="4" style="padding:0 5.4pt; vertical-align:top">
          <p style="line-height:150%; font-size:10pt; margin-top:8pt;">
            <span class="label">Cliente:</span><span class="label-strong"> ${cliente}</span><br />
            <span class="label">Endereço:</span><span class="label-strong"> ${enderecoCliente}</span><br />
            <span class="label">Equipamento:</span><span class="label-strong"> ${equipamentoText}</span><br />
            <span class="label">Localização:</span><span class="label-strong"> ${localizacao}</span>
          </p>
        </td>
      </tr>
      <tr>
        <td colspan="4" style="padding:0 5.4pt; vertical-align:top">
          <p class="heading-section-title">
            Dados para atendimento
          </p>
        </td>
      </tr>
      <tr>
        <td style="width:12%; min-width:4.2em; white-space:nowrap; padding:4pt 2pt 4pt 5.4pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:10pt"><span class="label-strong">Previsão</span></p>
        </td>
        <td style="width:1%; white-space:nowrap; padding:4pt 8pt 4pt 5.4pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:10pt"><span class="label-strong">Solicitante</span></p>
        </td>
        <td colspan="2" style="padding:4pt 5.4pt 4pt 8pt; vertical-align:top; background-color:#f2f2f2">
          <p style="margin:0; font-size:10pt"><span class="label-strong">Defeito</span></p>
        </td>
      </tr>
      <tr>
        <td style="min-width:4.2em; white-space:nowrap; padding:4pt 2pt 4pt 5.4pt; vertical-align:top">
          <p style="margin:0; font-size:10pt"><span class="label">${previsaoLabel}</span></p>
        </td>
        <td style="width:1%; white-space:nowrap; padding:4pt 8pt 4pt 5.4pt; vertical-align:top">
          <p style="margin:0; font-size:10pt"><span class="label">${solicitanteText}</span></p>
        </td>
        <td colspan="2" style="padding:4pt 5.4pt 4pt 8pt; vertical-align:top">
          <p style="margin:0; font-size:10pt"><span class="label">${defeitoNome}${defeitoObservacao ? ' - ' + defeitoObservacao : ''}</span></p>
        </td>
      </tr>
    </table>

    <p class="heading-section-title" style="margin-top:14pt;">Laudo técnico</p>
    <table cellspacing="0" cellpadding="0" style="width:100%; border-collapse:collapse; margin-bottom:0; border:0.75pt solid #d9d9d9;">
      <tr>
        <td style="border-bottom:0.5pt solid #e5e5e5; padding:6pt 8pt 4pt 8pt; vertical-align:top;">
          <p style="margin:0; font-size:10pt; font-weight:bold;"><span class="label">Condição do equipamento após intervenção</span></p>
        </td>
      </tr>
      ${Array(4).fill(0).map(() => `
      <tr>
        <td style="border-bottom:0.5pt solid #e5e5e5; padding:0 8pt; height:22pt; vertical-align:top;"></td>
      </tr>`).join('')}
      <tr>
        <td style="border-bottom:0.5pt solid #e5e5e5; padding:6pt 8pt 4pt 8pt; vertical-align:top;">
          <p style="margin:0; font-size:10pt; font-weight:bold;"><span class="label">Observações</span></p>
        </td>
      </tr>
      ${Array(4).fill(0).map(() => `
      <tr>
        <td style="border-bottom:0.5pt solid #e5e5e5; padding:0 8pt; height:22pt; vertical-align:top;"></td>
      </tr>`).join('')}
    </table>

    <table cellspacing="0" cellpadding="0" style="width:100%; border-collapse:collapse; margin-top:8pt;">
      <tr>
        <td style="padding:6pt 0; font-size:10pt;">
          <span class="label">Data e hora inicial</span> ___/___/______ ___:___
        </td>
        <td style="padding:6pt 0; font-size:10pt; text-align:right;">
          <span class="label">Data e hora final</span> ___/___/______ ___:___
        </td>
      </tr>
    </table>

    <table cellspacing="0" cellpadding="0" style="width:100%; border-collapse:collapse; margin-top:18pt;">
      <tr>
        <td colspan="5" style="border-top:1.5pt solid #a5a5a5; padding:12pt 5.4pt 0 5.4pt; vertical-align:top;">
          <p style="margin:0; font-size:15pt;">
            <span class="label-strong" style="color:#6600ff">Técnico responsável</span>
          </p>
          <p style="margin:8pt 0 0 0; font-size:10pt;"><span class="label">Nome:</span></p>
        </td>
        <td colspan="3" style="border-top:1.5pt solid #a5a5a5; padding:12pt 5.4pt 0 5.4pt; vertical-align:top;">
          <p style="margin:0; font-size:15pt;">
            <span class="label-strong" style="color:#6600ff">Solicitante / cliente</span>
          </p>
          <p style="margin:8pt 0 0 0; font-size:10pt;"><span class="label">Nome:</span></p>
        </td>
      </tr>
      <tr style="height:40pt;">
        <td colspan="5" style="border-bottom:0.75pt solid #d9d9d9; padding:0 5.4pt; vertical-align:bottom;">
          <p style="margin:0; font-size:10pt;"><span class="label">Assinatura:</span></p>
        </td>
        <td colspan="3" style="border-bottom:0.75pt solid #d9d9d9; padding:0 5.4pt; vertical-align:bottom;">
          <p style="margin:0; font-size:10pt;"><span class="label">Assinatura:</span></p>
        </td>
      </tr>
    </table>
  </div>

  <div class="page">
    ${historicoTableBlock(historicoRowsHtml)}
  </div>
</body>
</html>
  `;
  return { html, osNumber: d.osNumber };
}

/** Estilo e estrutura da segunda página (histórico) para reuso. Mesmo A4 fixo para PDF sem distorção. */
const HISTORICO_PAGE_STYLE = `
  @page { margin: 8mm; }
  body { line-height:108%; font-family: Inter, Calibri, Arial, sans-serif; font-size:11pt; margin:0; padding:0; }
  .page { page-break-after: always; width: 210mm; height: 297mm; box-sizing: border-box; overflow: hidden; }
  .label { font-family: Inter, Calibri, Arial, sans-serif; }
  .label-strong { font-weight:bold; }
  table.historico-os-table { table-layout: fixed; width: 100%; border-collapse: collapse; }
`;

/**
 * Monta apenas a segunda página (Histórico de atendimento) filtrada por número de série.
 * Usado pela abertura de OS (OpenOSModal) para incluir a mesma segunda página do Board.
 * @param {string} serie - Número de série do equipamento
 * @returns {Promise<{ fullHtml: string, secondPageInnerHtml: string }>}
 */
export async function buildHistoricoPageHtml(serie) {
  const esc = (s) =>
    (s || '')
      .toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  let historicoRowsHtml = '';
  const trimmedSerie = (serie || '').toString().trim();
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;

  if (trimmedSerie && token) {
    try {
      const base = getApiBaseUrl();
      const res = await fetch(
        `${base}/api/oficina/ordens-serie?serie=${encodeURIComponent(trimmedSerie)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (res.ok) {
        const data = await res.json();
        const list = data && data.success && Array.isArray(data.data) ? data.data : [];
        const sorted = sortHistoricoByFechamentoDesc(list);
        const limited = sorted.slice(0, 6);
        historicoRowsHtml = buildHistoricoTableRowsHtml(limited, esc);
      }
    } catch (e) {
      console.error('Erro ao carregar histórico (abertura OS):', e);
    }
  }

  if (!historicoRowsHtml) {
    historicoRowsHtml = `
<tr>
  <td colspan="9" style="border:1.5pt solid #d0cece; padding:3pt; font-size:7pt; font-family:Inter; text-align:center;">Nenhum histórico de atendimento encontrado.</td>
</tr>`;
  }

  const secondPageInnerHtml = `
  <div class="page">
    ${historicoTableBlock(historicoRowsHtml)}
  </div>`;

  const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${HISTORICO_PAGE_STYLE}</style></head><body>${secondPageInnerHtml}</body></html>`;
  return { fullHtml, secondPageInnerHtml };
}

/**
 * Abre o layout principal (OS + histórico) em nova aba. Opcionalmente aciona impressão.
 * @param {object} order - Ordem atual (Board)
 * @param {{ openForPrint?: boolean }} opts - openForPrint: true para abrir e acionar diálogo de impressão
 */
export async function generateOSPdfV2FromOrder(order, opts = {}) {
  const { html, osNumber } = await buildOSPdfV2Html(order);
  const blob = await fetchOsPdfFromServer(html, `OS-${osNumber}.pdf`);
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (opts.openForPrint && w) {
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (_) {
        /* visualizador de PDF */
      }
    }, 500);
  }
  setTimeout(() => URL.revokeObjectURL(url), 180000);
}

/**
 * Monta HTML com múltiplas OS (cada uma: página da OS + página histórico). Mesmo layout de buildOSPdfV2Html.
 * @param {Array<object>} orders - Array de ordens (ex.: abertura de OS em lote)
 * @returns {Promise<string>} HTML completo
 */
export async function buildMultiOSPagesHtml(orders) {
  if (!orders || orders.length === 0) return '';
  const first = await buildOSPdfV2Html(orders[0]);
  if (orders.length === 1) return first.html;
  if (typeof document === 'undefined') return first.html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(first.html, 'text/html');
  const head = doc.querySelector('head');
  const body = doc.querySelector('body');
  let allPagesHtml = body ? body.innerHTML : '';
  for (let i = 1; i < orders.length; i++) {
    const next = await buildOSPdfV2Html(orders[i]);
    const nextDoc = parser.parseFromString(next.html, 'text/html');
    const pages = nextDoc.querySelectorAll('.page');
    for (const p of pages) allPagesHtml += p.outerHTML;
  }
  return `<!DOCTYPE html><html><head>${head ? head.innerHTML : ''}</head><body>${allPagesHtml}</body></html>`;
}

/**
 * Gera e faz download de um PDF com múltiplas OS (mesmo layout do detalhe).
 * @param {Array<object>} orders - Array de ordens
 */
export async function generateOSPdfV2DownloadMultiple(orders) {
  const html = await buildMultiOSPagesHtml(orders);
  if (!html) return;
  const firstOS = orders[0] && (orders[0].id != null ? String(orders[0].id) : '000000');
  const lastOS = orders.length > 1 && orders[orders.length - 1] && orders[orders.length - 1].id != null ? String(orders[orders.length - 1].id) : firstOS;
  const filename = orders.length === 1 ? `OS-${firstOS}.pdf` : `OS-${firstOS}_a_${lastOS}.pdf`;
  const blob = await fetchOsPdfFromServer(html, filename);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Abre o documento com múltiplas OS e opcionalmente aciona impressão.
 * @param {Array<object>} orders - Array de ordens
 * @param {{ openForPrint?: boolean }} opts
 */
export async function generateOSPdfV2FromOrderMultiple(orders, opts = {}) {
  const html = await buildMultiOSPagesHtml(orders);
  if (!html) return;
  const firstOS = orders[0] && (orders[0].id != null ? String(orders[0].id) : '000000');
  const lastOS = orders.length > 1 && orders[orders.length - 1] && orders[orders.length - 1].id != null ? String(orders[orders.length - 1].id) : firstOS;
  const filename = orders.length === 1 ? `OS-${firstOS}.pdf` : `OS-${firstOS}_a_${lastOS}.pdf`;
  const blob = await fetchOsPdfFromServer(html, filename);
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (opts.openForPrint && w) {
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (_) {
        /* visualizador de PDF */
      }
    }, 500);
  }
  setTimeout(() => URL.revokeObjectURL(url), 180000);
}

/**
 * Gera e faz download de um PDF com o mesmo layout do Imprimir (OS + histórico).
 * @param {object} order - Ordem atual (Board)
 */
// A4 em px (96dpi) para captura com proporção exata e sem distorção (exportado para OpenOSModal)
export const A4_PX_W = Math.round((210 / 25.4) * 96);
export const A4_PX_H = Math.round((297 / 25.4) * 96);

export async function generateOSPdfV2Download(order) {
  const { html, osNumber } = await buildOSPdfV2Html(order);
  const blob = await fetchOsPdfFromServer(html, `OS-${osNumber}.pdf`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `OS-${osNumber}.pdf`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Abre o documento no layout principal (OS + histórico) e aciona o diálogo de impressão.
 * @param {object} order - Ordem (ex.: ordem do Board)
 */
export function printOSFromOrder(order) {
  generateOSPdfV2FromOrder(order, { openForPrint: true });
}
